import React, { useState, useRef, useEffect, useCallback } from "react";
import EmojiPicker from "emoji-picker-react";
import "./Posts.css";
import "./EditPosts.css";
import p from './images/profile.jpg';
import { useOutletContext } from 'react-router-dom';

const SLOW_THRESHOLD_MS = 2000;
function _checkSlow(label, startTime) {
    const elapsed = Date.now() - startTime;
    if (elapsed > SLOW_THRESHOLD_MS)
        console.error(`[SLOW] ${label} took ${elapsed}ms (>${SLOW_THRESHOLD_MS}ms).`);
    else
        console.debug(`[TIMING] ${label} — ${elapsed}ms`);
}

// ── Module-level cache shared with Store.js ───────────────────────────────────
// Posts.js was re-fetching profile_image even though Store.js already fetched
// it via get_store_header. We now read from the same in-memory cache instead.
const _cache = new Map();
const CACHE_TTL = 5 * 60 * 1000;
const cacheGet = (k) => { const e = _cache.get(k); return (e && Date.now() < e.exp) ? e.v : null; };
const cacheSet = (k, v) => _cache.set(k, { v, exp: Date.now() + CACHE_TTL });

// ── Posts cache for instant render on repeat visits ─────────────────────────
// KEY FIX: use the same cache key as Store.js ("page_posts:{id}") so that
// Store's eager prefetch and this component share a single cached result.
const _postsCache = new Map();
const POSTS_CACHE_TTL = 5 * 60 * 1000;
const postsCacheGet = (k) => { const e = _postsCache.get(k); return (e && Date.now() < e.exp) ? e.v : null; };
const postsCacheSet = (k, v) => _postsCache.set(k, { v, exp: Date.now() + POSTS_CACHE_TTL });

const storageGet = (key) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const { val, exp } = JSON.parse(raw);
        return Date.now() < exp ? val : null;
    } catch { return null; }
};

const storageSet = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify({ val: value, exp: Date.now() + POSTS_CACHE_TTL }));
    } catch {}
};

// Unified key — matches Store.js cacheSet(`page_posts:${pageid}`, d)
const getPostsCacheKey = (pageId) => `page_posts:${pageId}`;

const readCachedPosts = (pageId) => {
    if (!pageId) return null;
    const key = getPostsCacheKey(pageId);
    return postsCacheGet(key) || storageGet(key);
};

const writeCachedPosts = (pageId, value) => {
    const key = getPostsCacheKey(pageId);
    postsCacheSet(key, value);
    storageSet(key, value);
};

function Posts() {
    const [showComments,        setShowComments]        = useState({});
    const [commentText,         setCommentText]         = useState({});
    const [emojiPickerVisible,  setEmojiPickerVisible]  = useState({});
    const { pageId, userId, postsData: ctxPostsData, profileImage: ctxProfileImage } = useOutletContext();

    // Priority: Outlet context (Store.js eager prefetch) → localStorage → nothing.
    // ctxPostsData is populated by Store.js which fires the fetch at module-load
    // time — by the time this component mounts it is usually already resolved.
    const initialPostsCache = (() => {
        if (ctxPostsData?.posts?.length) return ctxPostsData;
        return readCachedPosts(pageId);
    })();

    // True when Store already delivered fresh data — no fetch needed on mount.
    const ctxIsFresh = Boolean(ctxPostsData?.posts?.length);

    const [posts,               setPosts]               = useState(initialPostsCache?.posts || []);
    const [loading,             setLoading]             = useState(!initialPostsCache);
    const [error,               setError]               = useState(null);
    const [likeCounts,          setLikeCounts]          = useState(initialPostsCache?.likeCounts || {});
    const [likedPosts,          setLikedPosts]          = useState(initialPostsCache?.likedPosts || {});
    const [isSubmittingComment, setIsSubmittingComment] = useState({});


    // NOTE: Header is NOT rendered here — Store.js already renders it.
    // Rendering it again caused a duplicate navbar and added extra layout cost.


    const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

    // Prefer profile image already resolved by Store's header fetch — avoids
    // a redundant /api/get_page_profile_image round-trip inside Posts.
    const [profileImage,        setProfileImage]        = useState(
        ctxProfileImage || initialPostsCache?.profileImage || p
    );
   

    const fetchPosts = useCallback(async (currentPageId, background = false) => {
        if (!currentPageId) return;
        const t0 = Date.now();
        if (!background) setLoading(true);
        setError(null);

        try {
            // Profile image: prefer Store's already-resolved header cache.
            // This eliminates the redundant /api/get_page_profile_image round-trip
            // because Store.js fetches the header (which includes profile_image_url)
            // at module-load time and stores it under `header:{pageId}`.
            const cachedHeader = cacheGet(`header:${currentPageId}`);
            const profileUrl   = ctxProfileImage
                || cachedHeader?.profile_image_url
                || null;

            // Only hit the separate profile-image endpoint as a last resort
            const requests = [fetch(`${API_URL}/page/${currentPageId}/posts`)];
            if (!profileUrl) {
                requests.push(fetch(`${API_URL}/api/get_page_profile_image?pageid=${currentPageId}`));
            }

            const [postsRes, imgRes] = await Promise.all(requests);
            _checkSlow(`GET /page/${currentPageId}/posts`, t0);

            let postsData = [];
            let likeCountsObj = {};
            let likedPostsObj = {};

            if (postsRes.ok) {
                const data  = await postsRes.json();
                postsData = data.posts || [];
                setPosts(postsData);
                postsData.forEach(post => {
                    likeCountsObj[post.postid] = post.likes     || 0;
                    likedPostsObj[post.postid] = post.has_liked || false;
                });
                setLikeCounts(likeCountsObj);
                setLikedPosts(likedPostsObj);
            } else {
                setError("Error fetching posts.");
            }

            // Resolve final profile URL and persist everything to cache
            const finalProfileUrl = profileUrl || (imgRes?.ok ? (await imgRes.json()).profile_image_url : null) || p;
            if (finalProfileUrl !== p) setProfileImage(finalProfileUrl);
            writeCachedPosts(currentPageId, {
                posts: postsData,
                likeCounts: likeCountsObj,
                likedPosts: likedPostsObj,
                profileImage: finalProfileUrl,
            });

        } catch (err) {
            console.error("[Posts] fetch error:", err);
            setError("An error occurred. Please check your connection.");
        } finally {
            setLoading(false);
        }
    }, [pageId, API_URL, ctxProfileImage]);

    useEffect(() => {
        if (!pageId) return;
        if (ctxIsFresh) {
            // Store's eager prefetch already delivered fresh data — no fetch at
            // all on first mount. Background revalidation runs after 30s so the
            // user sees content instantly with no extra network request.
            const id = setTimeout(() => fetchPosts(pageId, true), 30_000);
            return () => clearTimeout(id);
        }
        if (initialPostsCache) {
            // Stale cache — show it immediately, revalidate in background.
            fetchPosts(pageId, true);
        } else {
            fetchPosts(pageId);
        }
    }, [fetchPosts, pageId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Like / Unlike ─────────────────────────────────────────────────────────
    const handleLikeClick = async (postid) => {
        const t0 = Date.now();
        try {
            const response = await fetch(`${API_URL}/page/post/like/${postid}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: userId }),
            });
            if (response.ok) {
                const data = await response.json();
                setLikeCounts(prev => ({ ...prev, [postid]: data.likes }));
                setLikedPosts(prev => ({ ...prev, [postid]: !prev[postid] }));
                _checkSlow(`POST /page/post/like/${postid}`, t0);
            } else {
                setError("Error updating likes.");
            }
        } catch {
            setError("Error updating likes.");
        }
    };

    // ── Comment submit ────────────────────────────────────────────────────────
    const handleCommentSubmit = async (e, postid) => {
        e.preventDefault();
        if (!commentText[postid]?.trim()) return;
        const t0 = Date.now();
        setIsSubmittingComment(prev => ({ ...prev, [postid]: true }));
        try {
            const formData = new FormData();
            formData.append('postid', postid);
            formData.append('user_id', userId);
            formData.append('comment_text', commentText[postid]);
            const response = await fetch(`${API_URL}/page/post/comment`, {
                method: 'POST',
                body: formData,
            });
            if (response.ok) {
                const data = await response.json();
                setPosts(prev => prev.map(post =>
                    post.postid === postid ? { ...post, comments: data.comments } : post
                ));
                setCommentText(prev => ({ ...prev, [postid]: "" }));
                setEmojiPickerVisible(prev => ({ ...prev, [postid]: false }));
                _checkSlow(`POST /page/post/comment postid=${postid}`, t0);
            } else {
                setError("Error adding comment.");
            }
        } catch {
            setError("An error occurred while adding the comment.");
        } finally {
            setIsSubmittingComment(prev => ({ ...prev, [postid]: false }));
        }
    };

    // ── Fetch comments on demand ──────────────────────────────────────────────
    const fetchComments = async (postid) => {
        try {
            const response = await fetch(`${API_URL}/page/post/comments/${postid}`);
            if (response.ok) {
                const data = await response.json();
                setPosts(prev => prev.map(post =>
                    post.postid === postid ? { ...post, comments: data.comments } : post
                ));
            }
        } catch { }
    };

    const handleCommentInputChange = (e, postid) => {
        setCommentText(prev => ({ ...prev, [postid]: e.target.value }));
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="card-container-edit-store">
            {loading ? (
                <p>Loading...</p>
            ) : error ? (
                <p>{error}</p>
            ) : posts.length === 0 ? (
                <div className="no-posts-state">
                    <div className="no-posts-icon">📭</div>
                    <h3>No Posts Yet</h3>
                    <p>This page hasn't shared any posts yet.</p>
                </div>
            ) : (
                posts.map((post, index) => (
                    <div key={post.postid ?? index} className="card-edit-store">
                        <div className="card-body-edit">
                            <div className="Posts_header-edit">
                                <div className="logo-and-text-edit">
                                    <img
                                        src={profileImage}
                                        alt="Profile"
                                        className="logo-image-edit"
                                        onError={e => { e.target.src = p; }}
                                    />
                                    <div className="Posts_paragraph-edit">
                                        <p>{post.page_name}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="Posts_content-edit">
                                <p>{post.post_content}</p>
                            </div>

                            {post.image_url && (
                                <img
                                    src={post.image_url}
                                    alt="Uploaded"
                                    className="Posts_image-edit"
                                    loading="lazy"
                                />
                            )}
                            {post.video_url && (
                                <video controls className="uploaded-video-edit">
                                    <source src={post.video_url} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            )}

                            <div className="post-actions-edit">
                                <button
                                    className="action-btn-edit"
                                    onClick={() => handleLikeClick(post.postid)}
                                >
                                    {likedPosts[post.postid]
                                        ? `👍 Unlike • ${likeCounts[post.postid] ?? ""}`
                                        : `👍 Like • ${likeCounts[post.postid] ?? ""}`}
                                </button>
                                <button
                                    className="action-btn-edit"
                                    onClick={() => {
                                        setShowComments(prev => ({
                                            ...prev,
                                            [post.postid]: !prev[post.postid],
                                        }));
                                        fetchComments(post.postid);
                                    }}
                                >
                                    💬 Comment
                                </button>
                            </div>
                        </div>

                        {showComments[post.postid] && (
                            <div className="comments-window-edit">
                                <div className="comments-content-edit">
                                    <div className="comments-header-edit">
                                        <h3>Comments</h3>
                                        <button
                                            className="close-btn-edit"
                                            onClick={() =>
                                                setShowComments(prev => ({
                                                    ...prev,
                                                    [post.postid]: false,
                                                }))
                                            }
                                        >
                                            ✖
                                        </button>
                                    </div>
                                    <div className="comments-list-edit">
                                        {post.comments?.map((comment, idx) => (
                                            <div key={idx} className="comment-card-new">
                                                <img
                                                    src={comment.profile_image || p}
                                                    className="comment-avatar-new"
                                                    onError={e => { e.target.src = p; }}
                                                />
                                                <div className="comment-content-new">
                                                    <span className="comment-username-new">
                                                        {comment.username}
                                                    </span>
                                                    <span className="comment-text-new">
                                                        {comment.comment_text}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <form
                                        className="comments-form-edit"
                                        onSubmit={e => handleCommentSubmit(e, post.postid)}
                                    >
                                        <div className="comment-input-modern">
                                            <input
                                                type="text"
                                                placeholder="Write a comment..."
                                                value={commentText[post.postid] || ""}
                                                onChange={e => handleCommentInputChange(e, post.postid)}
                                            />
                                            <button
                                                type="button"
                                                className="emoji-btn-new"
                                                onClick={() =>
                                                    setEmojiPickerVisible(prev => ({
                                                        ...prev,
                                                        [post.postid]: !prev[post.postid]
                                                    }))
                                                }
                                            >
                                                😊
                                            </button>
                                            <button type="submit" disabled={isSubmittingComment[post.postid]}>
                                                {isSubmittingComment[post.postid] ? "Posting..." : "Post"}
                                            </button>
                                        </div>

                                        {emojiPickerVisible[post.postid] && (
                                            <div className="emoji-picker-modern">
                                                <button
                                                    type="button"
                                                    className="emoji-picker-close"
                                                    onClick={() =>
                                                        setEmojiPickerVisible(prev => ({
                                                            ...prev,
                                                            [post.postid]: false
                                                        }))
                                                    }
                                                >
                                                    ×
                                                </button>
                                                <EmojiPicker
                                                    onEmojiClick={(emojiData) => {
                                                        setCommentText(prev => ({
                                                            ...prev,
                                                            [post.postid]: (prev[post.postid] || "") + emojiData.emoji
                                                        }));
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                ))
            )}
        </div>
    );
}

export default Posts;